import { ref } from 'vue'

/**
 * Interface global do Google Translate
 * Estende o objeto Window com as propriedades do Google Translate
 */
declare global {
  interface Window {
    google?: {
      translate: {
        TranslateElement: new (
          config: GoogleTranslateConfig,
          elementId: string,
        ) => void
      }
    }
    googleTranslateElementInit?: () => void
  }
}

interface GoogleTranslateConfig {
  pageLanguage: string
  includedLanguages: string
  autoDisplay: boolean
}

// Conta a tentativa inicial mais duas novas tentativas em caso de falha.
const MAX_TRANSLATION_ATTEMPTS = 3
const RETRY_DELAY_MS = 300

/**
 * Composable para gerenciar tradução via Google Translate Widget
 *
 * Implementa loading overlay até tradução completar, evitando
 * "flash" de conteúdo em português antes da tradução.
 *
 * @example
 * ```ts
 * const { isTranslating, translationReady, initTranslation } = useGoogleTranslate()
 *
 * onMounted(() => {
 *   initTranslation('es_PE')
 * })
 * ```
 */
export function useGoogleTranslate() {
  const isTranslating = ref(false)
  const translationReady = ref(false)

  /**
   * Inicializa o processo de tradução
   *
   * @param targetLocale - Locale alvo (ex: 'es_PE', 'es-ES', 'pt-BR')
   */
  function initTranslation(targetLocale: string) {
    if (!targetLocale || targetLocale === 'pt-BR' || targetLocale === 'pt') {
      translationReady.value = true
      return
    }

    isTranslating.value = true

    const runAttempt = (attempt: number) => {
      let attemptFinished = false
      let stopWatching = () => {}

      const finishAttempt = (translated: boolean) => {
        // Pode haver falha do script, do seletor e do observer quase ao mesmo
        // tempo; processa apenas o primeiro resultado desta tentativa.
        if (attemptFinished) return
        attemptFinished = true
        stopWatching()

        if (translated) {
          setTimeout(() => {
            isTranslating.value = false
            translationReady.value = true
          }, 300)
          return
        }

        if (attempt < MAX_TRANSLATION_ATTEMPTS) {
          // Repete o fluxo original de seleção do idioma no widget.
          setTimeout(() => runAttempt(attempt + 1), RETRY_DELAY_MS)
          return
        }

        // Após a última falha, preserva o fallback original: libera a página.
        setTimeout(() => {
          isTranslating.value = false
          translationReady.value = true
        }, 300)
      }

      // Cada tentativa observa sua própria conclusão ou timeout.
      stopWatching = watchTranslationComplete(finishAttempt)

      // Carrega Google Translate se ainda não estiver carregado
      if (!window.google?.translate) {
        loadGoogleTranslateScript(targetLocale, () => finishAttempt(false))
      } else {
        autoTranslate(targetLocale, () => finishAttempt(false))
      }
    }

    runAttempt(1)
  }

  /**
   * Carrega o script do Google Translate dinamicamente
   */
  function loadGoogleTranslateScript(
    targetLocale: string,
    onError: () => void,
  ) {
    // Define callback global
    window.googleTranslateElementInit = () => {
      if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'pt',
            includedLanguages: 'es,pt,en',
            autoDisplay: false,
          },
          'google_translate_element',
        )

        setTimeout(() => autoTranslate(targetLocale, onError), 1000)
      }
    }

    const script = document.createElement('script')
    script.src =
      '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
    script.async = true
    script.onerror = () => {
      script.remove()
      // Informa a tentativa atual para que ela possa ser repetida.
      onError()
    }
    document.head.appendChild(script)
  }

  /**
   * Força tradução automática selecionando o idioma no widget
   */
  function autoTranslate(locale: string, onError: () => void) {
    const langCode = locale.split(/[_-]/)[0].toLowerCase()
    let selectFound = false

    // Aguarda select do Google Translate estar disponível
    const interval = setInterval(() => {
      const select = document.querySelector(
        '.goog-te-combo',
      ) as HTMLSelectElement

      if (select) {
        selectFound = true
        clearInterval(interval)
        clearTimeout(timeout)

        // Define idioma e dispara evento de mudança
        select.value = langCode
        select.dispatchEvent(new Event('change'))
      }
    }, 100)

    const timeout = setTimeout(() => {
      clearInterval(interval)
      // Ausência do seletor impede a tradução; conta como falha da tentativa.
      if (!selectFound) onError()
    }, 5000)
  }

  /**
   * Observa o DOM para detectar quando a tradução completou
   * Google Translate adiciona classe 'translated-ltr' ou 'translated-rtl' ao <html>
   */
  function watchTranslationComplete(callback: (translated: boolean) => void) {
    let finished = false

    const finish = (translated: boolean) => {
      if (finished) return
      finished = true
      observer.disconnect()
      clearTimeout(timeout)
      callback(translated)
    }

    const observer = new MutationObserver(() => {
      const html = document.documentElement

      if (
        html.classList.contains('translated-ltr') ||
        html.classList.contains('translated-rtl')
      ) {
        finish(true)
      }
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    // Se não detectar a tradução em 5s, a tentativa atual falha.
    const timeout = setTimeout(() => {
      finish(false)
    }, 5000)

    return () => {
      finished = true
      observer.disconnect()
      clearTimeout(timeout)
    }
  }

  /**
   * Limpa tradução e volta para português
   */
  function resetTranslation() {
    const select = document.querySelector('.goog-te-combo') as HTMLSelectElement
    if (select) {
      select.value = ''
      select.dispatchEvent(new Event('change'))
    }

    isTranslating.value = false
    translationReady.value = true
  }

  return {
    isTranslating,
    translationReady,

    initTranslation,
    resetTranslation,
  }
}
